import * as Sentry from '@sentry/vue'
import OpenAIApi from '@/api/openai'
import SlideApi from '@/api/slide'
import utils from '@/util'
import i18nPresenter from '@/i18nPresenter'
import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'

// other actions
const UPDATE_SLIDE_BY_ID_ACTION = 'updateSlideByIdAction'
const SET_SNAPSHOT_BY_SLIDE_ID_ACTION = 'snapshot/setSnapshotAction'
const SET_IS_IMPROVING_WRITING = 'setIsImprovingWriting'

// HELPERS
const prepareInputDataToImproveSlideContent = (params) => {
  const { mode, value } = params
  return {
    writingStyle: {
      [mode]: value,
    },
  }
}

const validateSlideObject = (slideObject, slideId) => {
  if (!slideObject || !slideObject.body || !slideObject.body.id || slideObject.body.id !== slideId) return false
  return true
}

const fetchCanvasBlocks = async (canvasBlocksUrl) => {
  if (!canvasBlocksUrl) return null

  try {
    const canvasBlocks = await fetch(canvasBlocksUrl).then((res) => res.json()).then((body) => ({ body }))
    if (!canvasBlocks || !canvasBlocks.body) return null
    return canvasBlocks.body
  } catch (error) {
    Sentry.captureException(error)
    return null
  }
}

const updateCanvasBlocks = async (newCanvasBlocksUrl, dispatch, slideId) => {
  if (!newCanvasBlocksUrl) return

  const newCanvasBlocks = await fetchCanvasBlocks(newCanvasBlocksUrl)
  dispatch(UPDATE_SLIDE_BY_ID_ACTION, {
    id: slideId,
    canvasBlocks: newCanvasBlocks,
    canvasBlocksUrl: newCanvasBlocksUrl,
  }, { root: true })
}

const updateQuestionTitle = async (newQuestionTitle, newQuestionTitleHTML, dispatch, slideId) => {
  if (!newQuestionTitle || !newQuestionTitleHTML) return

  dispatch(UPDATE_SLIDE_BY_ID_ACTION, {
    id: slideId,
    title: newQuestionTitle,
    titleHTML: newQuestionTitleHTML,
  }, { root: true })
}

const updateSlideContentWithNewSlideObject = async (dispatch, slideObject, slideId) => {
  if (!slideObject) return

  const {
    canvasBlocksUrl: newCanvasBlocksUrl,
    title: newQuestionTitle,
    titleHTML: newQuestionTitleHTML,
  } = slideObject

  if (newCanvasBlocksUrl) {
    await updateCanvasBlocks(newCanvasBlocksUrl, dispatch, slideId)
  } else if (newQuestionTitle && newQuestionTitleHTML) {
    await updateQuestionTitle(newQuestionTitle, newQuestionTitleHTML, dispatch, slideId)
  }
}

const canRestoreOriginalSlide = (slideId, originalVersion) => {
  if (!slideId || !originalVersion) return false

  return originalVersion.id === slideId
}

const slideTypePayloadMap = {
  freestyle: (originalVersion) => ({ canvasBlocks: originalVersion.canvasBlocks }),
  default: (originalVersion) => ({
    title: originalVersion.title,
    titleHTML: originalVersion.titleHTML,
  }),
}

const getPayloadRestoreSlide = (slideType, originalVersion) => {
  const getPayload = slideTypePayloadMap[slideType] || slideTypePayloadMap.default
  return getPayload(originalVersion)
}

const _state = {
  isImprovingWriting: false,
}

const _getters = {
  canUseAIImproveTextBlockDirectly: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-improve-writing-directly')
  },
  isImprovingWriting: (state) => state.isImprovingWriting,
}

const actions = {
  async improveSlideContentAction({
    dispatch, rootState,
  }, params) {
    const slideId = rootState.slideActive && rootState.slideActive.id
    if (!slideId) return

    try {
      // call API improve slide content
      const improveWritingSettings = prepareInputDataToImproveSlideContent(params)

      const slideObject = await OpenAIApi.improveSlideContent({
        slideId,
        improveWritingSettings,
      })

      // check returned slideObject
      if (!validateSlideObject(slideObject, slideId)) return

      // take a snapshot for current slide
      dispatch(SET_SNAPSHOT_BY_SLIDE_ID_ACTION, { slideId }, { root: true })

      // update slide content
      await updateSlideContentWithNewSlideObject(dispatch, slideObject.body, slideId)

      utils.showToastSuccess(i18nPresenter.t('Slide content has been updated!'))
    } catch (error) {
      Sentry.captureException(error)
      utils.showToastError(i18nPresenter.t('Something went wrong on our side. Please try again.'))
    }
  },
  async improveTextBlockContentAction({ dispatch, rootState, commit }, params) {
    const { selectingBlocks } = rootState

    const slideId = rootState.slideActive && rootState.slideActive.id

    if (!slideId || (!selectingBlocks?.length)) return
    // Only process the first block, as we allow improving one block at a time
    const block = selectingBlocks[0]
    commit(SET_IS_IMPROVING_WRITING, true)
    const slideObject = await OpenAIApi.improveTextBlock({
      slideId,
      improveWritingSettings: {
        writingStyle: {
          writingMode: '',
          tone: params.value,
          complexity: '',
        },
      },
      canvasTextBlockId: block.id,
    })
    if (!validateSlideObject(slideObject, slideId)) return

    // take a snapshot for current slide
    dispatch(SET_SNAPSHOT_BY_SLIDE_ID_ACTION, { slideId }, { root: true })

    // update slide content
    await updateSlideContentWithNewSlideObject(dispatch, slideObject.body, slideId)
    commit(SET_IS_IMPROVING_WRITING, false)
    utils.showToastSuccess(i18nPresenter.t('Slide content has been updated!'))
  },

  // This logic is specifically used for the 'Improve Writing' feature.
  // Current undo/redo is not reusable as the logic is tied to the component through mixins
  // TO DO: Replace with new undo/redo functionality once it's ready.
  async restoreOriginalSlideAction({ dispatch, rootState, rootGetters }) {
    const originalVersion = rootGetters && rootGetters['snapshot/snapshot']
    const slideId = rootState.slideActive && rootState.slideActive.id
    const slideType = slideId && rootState.slideActive.type
    try {
      if (!canRestoreOriginalSlide(slideId, originalVersion)) return

      const payload = getPayloadRestoreSlide(slideType, originalVersion)

      // Back-end: Update slide content
      await SlideApi.updateSlideById(slideId, payload)

      // Front-end: Update slide content
      dispatch(UPDATE_SLIDE_BY_ID_ACTION, {
        id: slideId,
        ...payload,
      }, { root: true })
    } catch (error) {
      Sentry.captureException(error)
    }
  },
}

const mutations = {
  [SET_IS_IMPROVING_WRITING](state, isLoading) {
    state.isImprovingWriting = isLoading
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
